/**
 * Active alarms card — counts + per-alarm detail block.
 *
 * Backend shape:
 *   {
 *     status: 'ok' | 'unavailable',
 *     error: string | null,
 *     truncated: boolean,
 *     counts: {
 *       alarm, ok, insufficient_data,
 *       alarm_routed_standalone, alarm_unrouted, alarm_constituent,
 *     },
 *     in_alarm: Array<{
 *       name, state_value, state_reason, state_updated_timestamp,
 *       dimensions, last_6_datapoints,
 *       type: 'metric' | 'composite',
 *       is_constituent: boolean,
 *       routing: 'routed' | 'unrouted',
 *       muted_by_parent: boolean,   // absent from a route older than 2026-09-07
 *       firing_on: 'data' | 'missing_data' | 'unknown',
 *     }>
 *   }
 *
 * 2026-08-21 (U29): the backend used to read one unpaginated page of 100
 * alarms against 166 live udc-prod-* alarms, so six of the nine then in ALARM
 * were invisible here, on a green card. It now paginates and asks for
 * composite alarms as well. `truncated` reports a sweep that still hit its
 * page bound, so a partial count can never read as a complete one.
 *
 * ── Why this card groups instead of listing ─────────────────────────────────
 *
 * The ALARM count alone is not a to-do list. A composite constituent's state
 * belongs to its parent, so a permanent red there can be by design; an alarm
 * with no enabled action told nobody; and an alarm firing on missing data is
 * reporting silence, not a crossed threshold. A flat list that mixes those in
 * with the reds that want a human teaches the reader to ignore the list —
 * which would undo the fix that made the list visible in the first place. So
 * the card answers the operator's actual question first: WHICH OF THESE IS
 * MINE.
 *
 * For scale, a dated figure: on 2026-08-21, the day the page stopped reading
 * zero, PROD had nine alarms in ALARM — two composite constituents and three
 * with no action at all (they reached nobody), leaving four that wanted a
 * human; three of the nine were firing on missing data. The actionless ones
 * have since been wired (#2752), and a live PROD read on 2026-09-07 found no
 * standalone unrouted red. The mix changes; the grouping does not.
 *
 * The definitions live in the backend (backend/src/routes/admin-system-health.ts,
 * fetchAlarms). `routing: 'routed'` means an ENABLED AlarmActions entry — the
 * alarm's own, or a parent composite's that this alarm alone can fire — the
 * same reachability scripts/alarm-delivery-check.mjs computes. It does NOT
 * mean a confirmed subscriber on the topic; only a reader of SNS can see that.
 * The nightly detector for actionless alarms is scripts/env-drift-check.mjs
 * `alarmActions`; this card is a mirror of DescribeAlarms, not the control.
 *
 * `muted_by_parent` is the route's verdict on which constituents are muted,
 * and this card groups on it — one rule, decided once. Membership alone is
 * not that rule: a constituent with an enabled action of its own pages on its
 * own (udc-prod-stalled-connect-funnel, red from 2026-06-04 with SNS paging
 * while this card said its parent owned it; removed 2026-10-03, #3292), and a
 * constituent whose every
 * parent is unrouted has nobody upstream who will act (its only parent is a
 * scaffold with ActionsEnabled=false). Both are judged by their own routing.
 * Only a constituent with no action of its own AND a parent that can notify
 * is muted. When the field is absent — an older route behind this card — the
 * card falls back to membership, so either deploy order is safe.
 */
import { formatDate } from '@/lib/utils';
import { TileMeta, TileUnavailable, isUnavailable, tileShell } from './TileStatus';

interface Alarm {
  name?: string;
  state_value?: string;
  state_reason?: string;
  state_updated_timestamp?: string;
  dimensions?: Record<string, string>;
  type?: 'metric' | 'composite';
  is_constituent?: boolean;
  routing?: 'routed' | 'unrouted';
  muted_by_parent?: boolean;
  firing_on?: 'data' | 'missing_data' | 'unknown';
}

interface AlarmsData extends TileMeta {
  counts: {
    alarm: number;
    ok: number;
    insufficient_data: number;
    alarm_routed_standalone?: number;
    alarm_unrouted?: number;
    alarm_constituent?: number;
  };
  in_alarm: Alarm[];
  truncated?: boolean;
}

function Badge({ tone, title, children }: { tone: 'red' | 'amber' | 'grey'; title: string; children: string }) {
  const tones = {
    red: 'bg-red-500/10 text-red-700 border-red-500/30',
    amber: 'bg-amber-500/10 text-amber-800 border-amber-500/30',
    grey: 'bg-muted text-muted-foreground border-border',
  } as const;
  return (
    <span
      title={title}
      className={`inline-block rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wide ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

function AlarmRow({ a, muted }: { a: Alarm; muted: boolean }) {
  return (
    <div className={`bg-card border rounded p-3 ${muted ? 'opacity-70' : ''}`}>
      <div className={`font-mono text-sm font-semibold truncate ${muted ? '' : 'text-red-600'}`}>
        {muted ? '○' : '🔴'} {a.name}
      </div>
      <div className="flex flex-wrap gap-1.5 mt-1.5">
        {a.type === 'composite' && (
          <Badge tone="grey" title="A composite alarm — it aggregates other alarms.">
            composite
          </Badge>
        )}
        {a.is_constituent && muted && (
          <Badge
            tone="grey"
            title="Named in a composite alarm's rule and has no enabled action of its own. Its state belongs to that parent, which can notify, so a permanent red here can be by design."
          >
            constituent
          </Badge>
        )}
        {a.is_constituent && !muted && a.routing !== 'unrouted' && (
          <Badge
            tone="grey"
            title="Named in a composite alarm's rule, but it has an enabled action of its own — it pages on its own, whatever its parent does."
          >
            constituent · pages itself
          </Badge>
        )}
        {a.is_constituent && !muted && a.routing === 'unrouted' && (
          <Badge
            tone="grey"
            title="Named in a composite alarm's rule, but no parent composite can notify — none has an enabled action, own or inherited — so nobody upstream will act on this state."
          >
            constituent · parent silent
          </Badge>
        )}
        {a.routing === 'unrouted' && (
          <Badge
            tone="amber"
            title="No enabled alarm action — none of its own, and none on a parent composite this alarm alone can fire. It is firing and nobody was told."
          >
            no enabled action
          </Badge>
        )}
        {a.firing_on === 'missing_data' && (
          <Badge
            tone="amber"
            title="Firing because no datapoints arrived, with missing data treated as breaching — silence, not a crossed threshold."
          >
            no datapoints
          </Badge>
        )}
      </div>
      {a.state_updated_timestamp && (
        <div className="text-xs text-muted-foreground mt-1.5">
          In ALARM since {formatDate(a.state_updated_timestamp)}
        </div>
      )}
      {a.state_reason && (
        <div className="text-xs italic text-muted-foreground mt-1">{a.state_reason}</div>
      )}
      {a.dimensions && Object.keys(a.dimensions).length > 0 && (
        <div className="text-xs text-muted-foreground mt-1 font-mono">
          {Object.entries(a.dimensions)
            .map(([k, v]) => `${k}=${v}`)
            .join(', ')}
        </div>
      )}
    </div>
  );
}

function Group({
  title,
  hint,
  alarms,
  muted = false,
}: {
  title: string;
  hint: string;
  alarms: Alarm[];
  muted?: boolean;
}) {
  if (alarms.length === 0) return null;
  return (
    <div className="space-y-2">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide">
          {title} ({alarms.length})
        </div>
        <div className="text-xs text-muted-foreground">{hint}</div>
      </div>
      {alarms.map((a) => (
        <AlarmRow key={a.name} a={a} muted={muted} />
      ))}
    </div>
  );
}

export function ActiveAlarmsCard({ data }: { data: AlarmsData }) {
  const unavailable = isUnavailable(data);
  const counts = data?.counts ?? { alarm: 0, ok: 0, insufficient_data: 0 };
  const inAlarm = data?.in_alarm ?? [];

  // The route decides muting (`muted_by_parent`) — one rule for route and
  // card. A route older than that field sends none; fall back to plain
  // membership then, so an old route behind a new card still groups sanely.
  const isMuted = (a: Alarm) => a.muted_by_parent ?? Boolean(a.is_constituent);
  const needsOperator = inAlarm.filter((a) => !isMuted(a) && a.routing !== 'unrouted');
  const unrouted = inAlarm.filter((a) => !isMuted(a) && a.routing === 'unrouted');
  const constituents = inAlarm.filter(isMuted);

  // Muted constituents alone must not redden the card — their state belongs
  // to a composite that can notify, and it has its own row here when it fires.
  const hasAlarm = !unavailable && needsOperator.length + unrouted.length > 0;
  const n = (v: number) => (unavailable ? '—' : v);

  return (
    <section
      className={`border rounded-lg p-6 ${tileShell(
        unavailable,
        hasAlarm ? 'border-destructive bg-destructive/5' : 'border-green-500/40 bg-green-500/5',
      )}`}
    >
      <div className="flex items-center justify-between mb-3 gap-4 flex-wrap">
        <h3 className="text-lg font-semibold">Active alarms</h3>
        <div className="flex items-center gap-4 text-sm">
          <span className={hasAlarm ? 'text-red-600 font-medium' : 'text-muted-foreground'}>
            {hasAlarm ? '🔴' : '✓'} {n(counts.alarm)} ALARM
          </span>
          <span className="text-muted-foreground">✓ {n(counts.ok)} OK</span>
          <span className="text-muted-foreground">
            ⚪ {n(counts.insufficient_data)} INSUFFICIENT_DATA
          </span>
        </div>
      </div>

      {unavailable && <TileUnavailable error={data?.error} />}

      {data?.truncated && !unavailable && (
        <p className="text-xs text-amber-700 mb-3">
          ⚠ The alarm sweep hit its page limit — this is a partial count.
        </p>
      )}

      {unavailable ? null : inAlarm.length === 0 ? (
        <p className="text-sm text-muted-foreground">All CloudWatch alarms currently OK.</p>
      ) : (
        <div className="space-y-5">
          <Group
            title="Needs an operator"
            hint="Firing on this environment with an enabled notification action, its own or a parent composite's. Whether that action's topic has a confirmed subscriber is env-drift's check, not this page's."
            alarms={needsOperator}
          />
          <Group
            title="Firing, no enabled action"
            hint="No enabled alarm action, own or inherited. The condition is real; no one was told. The nightly control for this is env-drift-check alarmActions."
            alarms={unrouted}
          />
          <Group
            title="Composite constituents"
            hint="No enabled action of its own, and a parent composite that can notify owns the state. A permanent red here can be by design. A constituent that pages on its own, or whose parents cannot notify, is listed above instead."
            alarms={constituents}
            muted
          />
        </div>
      )}
    </section>
  );
}
